// Start-screen dashboard: the shortcut grid, the recents row, the centre search
// box, and clock/greeting visibility - all driven by settings.
import { shortcutsEl, recentsEl, jumpbackEl, clockEl, dateEl, greetingEl, startSearch, startSearchInput } from "./dom.js";
import { openBook, openFolder } from "./folders.js";
import { warmPlayPass } from "./play-pass.js";
import { openInternal, newTab } from "./tabs.js";
import { navigate, navigateTab } from "./navigation.js";
import { ICONS, ALL_NAME, ALL_SHORT, tilesOnStart, openShortcut, openAll, attachTileMenu, justLongPressed } from "./all-shortcuts.js";
import { attachAutofill } from "./autofill.js";
import * as history from "./history.js";
import * as bookRecents from "./book-recents.js";
import * as toolRecents from "./tool-recents.js";
import { TOOLS } from "../shared/tools-manifest.js";
import { readJSON, writeJSON, subscribe as storeSubscribe } from "../shared/store.js";
import { get, subscribe } from "../shared/settings-store.js";
import { lastHall, onOnline } from "./presence.js";
import { getSession } from "./account.js";
import { tileText } from "../shared/hall-rollout.js";
import { trustStrip } from "../shared/privacy-copy.js";

const X_SVG = '<svg width="9" height="9" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><line x1="4" y1="4" x2="10" y2="10"/><line x1="10" y1="4" x2="4" y2="10"/></svg>';

function tile(id, label, onOpen, shown = label) {
	const wrap = document.createElement("div"); wrap.className = "rv-shortcut";
	const btn = document.createElement("button"); btn.type = "button"; btn.className = "rv-shortcut-btn"; btn.setAttribute("aria-label", label);
	btn.innerHTML = ICONS[id] || "";
	btn.addEventListener("click", () => { if (!justLongPressed()) onOpen(); });
	const text = document.createElement("span"); text.textContent = shown; text.setAttribute("aria-hidden", "true");
	wrap.append(btn, text);
	return [wrap, btn];
}

// The shortcut tiles the student kept, then All shortcuts, which is always there (even with the
// row switched off) so a removed shortcut can always be found again.
export function renderShortcuts() {
	if (!shortcutsEl) return;
	// Rebuilding the row must not drop keyboard focus: keep it on the same tile, or on All shortcuts
	// when that tile was just removed.
	const had = shortcutsEl.contains(document.activeElement) ? document.activeElement.getAttribute("aria-label") : null;
	shortcutsEl.hidden = false;
	shortcutsEl.textContent = "";
	shortcutsEl.setAttribute("role", "group");
	shortcutsEl.setAttribute("aria-label", "Shortcuts");
	tilesOnStart().forEach((sc) => {
		const [wrap, btn] = tile(sc.id, sc.label, () => openShortcut(sc));
		if (sc.icon) btn.innerHTML = sc.icon; // a pinned tool
		if (sc.net) wrap.dataset.net = 1;
		btn.setAttribute("aria-keyshortcuts", "Shift+F10 Delete");
		btn.title = sc.label + ". Right-click or press and hold for options.";
		attachTileMenu(btn, sc);
		if (sc.id === "hall") { const note = document.createElement("b"); note.className = "rv-shortcut-note"; wrap.append(note); wrap.dataset.sc = "hall"; paintHallNote(wrap); paintHallBadge(wrap); }
		shortcutsEl.appendChild(wrap);
	});
	const [allWrap, allBtn] = tile("all", ALL_NAME, openAll, ALL_SHORT);
	allWrap.classList.add("rv-shortcut-all");
	allBtn.title = "Every shortcut, including ones you removed";
	shortcutsEl.appendChild(allWrap);
	if (had != null) {
		const same = [...shortcutsEl.querySelectorAll(".rv-shortcut-btn")].find((b) => b.getAttribute("aria-label") === had);
		(same || allBtn).focus({ preventScroll: true });
	}
}

// The Study Hall tile's "15 focusing": the rounded figure from the presence ping, only in beta/open
// and only for people who could get in. Positioned over the tile, so it never moves the grid.
function hallUsed() { try { return !!localStorage.getItem("revault:hall:used"); } catch { return false; } }
function paintHallNote(wrap) {
	const note = wrap && wrap.querySelector(".rv-shortcut-note");
	if (!note) return;
	const text = tileText(lastHall(), !!getSession(), hallUsed());
	note.textContent = text;
	note.hidden = !text;
}

// The unread dot (app/hall-badge.js). The tile's name says it too: "Study Hall, 1 mention".
let hallBadge = { dot: false, ment: 0 };
function paintHallBadge(wrap) {
	const btn = wrap && wrap.querySelector(".rv-shortcut-btn");
	if (!btn) return;
	let dot = wrap.querySelector(".rv-shortcut-dot");
	const on = hallBadge.dot || hallBadge.ment > 0;
	if (on && !dot) { dot = document.createElement("i"); dot.className = "rv-shortcut-dot"; dot.setAttribute("aria-hidden", "true"); wrap.append(dot); }
	if (dot) dot.hidden = !on;
	const base = btn.getAttribute("aria-label").replace(/, (?:\d+ mentions?|new messages)$/, "");
	btn.setAttribute("aria-label", hallBadge.ment > 0 ? base + ", " + hallBadge.ment + (hallBadge.ment === 1 ? " mention" : " mentions") : on ? base + ", new messages" : base);
}
export function setHallBadge(b) {
	hallBadge = { dot: !!b.dot, ment: Math.max(0, Number(b.ment) || 0) };
	const wrap = shortcutsEl && shortcutsEl.querySelector('.rv-shortcut[data-sc="hall"]');
	if (wrap) paintHallBadge(wrap);
}

// One quiet line at the bottom of the start screen; each part opens its paragraph on /privacy.

function renderTrust() {
	const foot = document.getElementById("rv-start-foot");
	if (!foot || foot.querySelector(".rv-start-trust")) return;
	foot.prepend(trustStrip(document, "rv-trust rv-start-trust", (url) => openInternal(url, "Privacy")));
	// its own line, so the strip stays the same three items everywhere
	const p = document.createElement("p");
	p.className = "rv-trust rv-start-what";
	const a = document.createElement("a");
	a.href = "/schools";
	a.textContent = "What is this?";
	a.addEventListener("click", (e) => { e.preventDefault(); openInternal("/schools", "For schools"); });
	// Owner decision D1 (2026-10-02): a quiet text link, never a banner. It lives here so it stays when
	// the This week card is hidden, and it's drawn before the slots load, so the start page's house
	// ad leaves out the same invite (slots/main.js houseFor).
	const com = document.createElement("a");
	com.className = "rv-home-community";
	com.textContent = "Community";
	com.href = "https://discord.gg/EAmRAAcSVx";
	com.target = "_blank";
	com.rel = "noopener noreferrer";
	p.append(com, " · ", a);
	foot.append(p);
}

export function renderRecents() {
	if (!recentsEl) return;
	const items = get("showRecents") !== false ? history.recent(8) : [];
	recentsEl.hidden = !items.length;
	recentsEl.textContent = "";
	if (!items.length) return;
	const list = document.createElement("div");
	list.className = "rv-recents-list";
	items.forEach((h) => {
		const pill = document.createElement("div");
		pill.className = "rv-recent";
		const go = document.createElement("button");
		go.className = "rv-recent-go"; go.title = h.title ? h.title + "\n" + h.url : h.url; go.textContent = h.title || h.url;
		go.addEventListener("click", () => { const id = newTab(); navigateTab(id, h.url); });
		const x = document.createElement("button");
		x.className = "rv-recent-x"; x.setAttribute("aria-label", "Remove " + (h.title || h.url) + " from recents"); x.title = "Remove"; x.innerHTML = X_SVG;
		x.addEventListener("click", (e) => { e.stopPropagation(); history.remove(h.url); renderRecents(); });
		pill.append(go, x);
		list.appendChild(pill);
	});
	const more = document.createElement("button");
	more.type = "button"; more.className = "rv-recent rv-recent-more"; more.textContent = "More";
	more.setAttribute("aria-label", "More recent sites, in History");
	more.addEventListener("click", () => openFolder("history"));
	recentsEl.append(list, more);
	fitRecents();
}

// Chips that wrapped onto the cut-off second line go away properly, so Tab can't land on them.
function fitRecents() {
	const list = recentsEl && recentsEl.firstChild;
	if (!list) return;
	const chips = [...list.children];
	chips.forEach((c) => { c.hidden = false; });
	if (chips.length) chips.forEach((c) => { c.hidden = c.offsetTop > chips[0].offsetTop; });
}

const BOOK_X = '<svg width="9" height="9" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><line x1="4" y1="4" x2="10" y2="10"/><line x1="10" y1="4" x2="4" y2="10"/></svg>';
const HOME_KEY = "revault:home";
const homeState = () => Object.assign({}, readJSON(HOME_KEY, {}));

// "Jump back in" (docs/HOME-2026-10.md, F3): the student's own recent things, newest
// first, at most 8. Open tasks lead; then books, tools (last 3) and the last studied deck.
export function continueItems(now = Date.now()) {
	const todos = readJSON("revault:todo", []);
	const open = Array.isArray(todos) ? todos.filter((t) => t && !t.done).length : 0;
	const rest = bookRecents.list().map((b) => ({ kind: "book", ts: b.ts || 0, b }));
	toolRecents.list().slice(0, 3).forEach((r) => { const t = TOOLS.find((x) => x.id === r.id); if (t) rest.push({ kind: "tool", ts: r.ts, t }); });
	const fc = readJSON("revault:flashcards", null);
	const decks = fc && Array.isArray(fc.decks) ? fc.decks.filter((d) => d && Number.isFinite(d.studiedAt) && Array.isArray(d.cards)) : [];
	const deck = decks.sort((a, b) => b.studiedAt - a.studiedAt)[0];
	if (deck && homeState().deckX !== deck.id) rest.push({ kind: "deck", ts: deck.studiedAt, d: deck });
	rest.sort((a, b) => b.ts - a.ts);
	return (open > 0 ? [{ kind: "tasks", n: open, ts: now }] : []).concat(rest).slice(0, 8);
}

const mk = (tag, cls, text) => { const e = document.createElement(tag); e.className = cls; if (text != null) e.textContent = text; return e; };
const letter = (cover, s) => { cover.classList.add("noimg"); cover.textContent = (s || "?").charAt(0).toUpperCase(); };
// The letter shows until a real picture has loaded, so a tile is never a blank square.
function coverImage(cover, src, name) {
	letter(cover, name);
	if (!src) return;
	const img = new Image();
	img.decoding = "async"; img.alt = "";
	img.addEventListener("load", () => { if (img.naturalWidth > 1) { cover.textContent = ""; cover.classList.remove("noimg"); cover.appendChild(img); } }, { once: true });
	img.src = src;
}
const toolIcon = (id) => (TOOLS.find((t) => t.id === id) || {}).icon;

// One tile: [label, cover (an icon string for a chip, or a painter), open, remove].
function tileOf(it) {
	if (it.kind === "book") {
		const b = it.b;
		return [b.name || "Untitled", (cover) => {
			if (typeof b.url === "string" && b.url.charAt(0) === "/") warmPlayPass(); // a pass before the click (an isolate title opens a window at once)
			coverImage(cover, b.thumb || b.cover, b.name);
		}, () => openBook(b), () => bookRecents.remove(b.url)];
	}
	const tool = (id, name) => () => openInternal("/tools/" + id, name); // like a Tools card
	if (it.kind === "tool") return [it.t.name, it.t.icon, tool(it.t.id, it.t.name), () => toolRecents.remove(it.t.id)];
	if (it.kind === "deck") {
		const n = it.d.cards.length;
		return [(it.d.name || "Untitled deck") + " · " + n + (n === 1 ? "\u00a0card" : "\u00a0cards"), toolIcon("flashcards"), tool("flashcards", "Flashcards"), () => writeJSON(HOME_KEY, Object.assign(homeState(), { deckX: it.d.id }))];
	}
	return [it.n === 1 ? "1 task open" : it.n + " tasks open", (cover) => cover.appendChild(mk("span", "rv-jumpback-n", it.n)), tool("todo", "To-do")];
}

// The same list as plain links, for the island's Panel (Island Mode has no start page tiles).
export const continueLinks = () => (get("showContinue") !== false ? continueItems() : []).map((it) => { const [label, , open] = tileOf(it); return { label, open }; });

export function renderContinue() {
	if (!jumpbackEl) return;
	const items = get("showContinue") !== false ? continueItems() : [];
	jumpbackEl.hidden = !items.length;
	jumpbackEl.textContent = "";
	if (!items.length) return;
	const rowEl = mk("div", "rv-jumpback-row");
	items.forEach((it) => {
		const [label, paint, open, drop] = tileOf(it);
		const wrap = mk("div", "rv-jumpback-item"), cardEl = mk("button", "rv-jumpback-card"), cover = mk("div", "rv-jumpback-cover");
		cover.setAttribute("aria-hidden", "true"); // the name is on the card
		cardEl.type = "button"; cardEl.title = label;
		if (typeof paint === "function") paint(cover);
		else { cover.classList.add("rv-jumpback-chip"); cover.innerHTML = paint; } // static icons from the tools list
		if (it.kind === "tasks") cover.classList.add("rv-jumpback-chip");
		cardEl.append(cover, mk("span", "rv-jumpback-name", label));
		cardEl.addEventListener("click", open);
		wrap.appendChild(cardEl);
		if (drop) {
			const x = mk("button", "rv-jumpback-x");
			x.type = "button"; x.setAttribute("aria-label", "Remove " + label); x.title = "Remove"; x.innerHTML = BOOK_X;
			x.addEventListener("click", (e) => { e.stopPropagation(); drop(); renderContinue(); });
			wrap.appendChild(x);
		}
		rowEl.appendChild(wrap);
	});
	jumpbackEl.append(mk("div", "rv-jumpback-head", "Jump back in"), rowEl);
}

function applyVisibility() {
	const hideClock = get("clockShow") === false;
	[clockEl, dateEl, greetingEl].forEach((el) => { if (el) el.hidden = hideClock; });
}

export function initStart() {
	if (startSearch) startSearch.addEventListener("submit", (e) => {
		e.preventDefault();
		const v = (startSearchInput.value || "").trim();
		if (!v) return;
		navigate(v);
		startSearchInput.value = "";
	});
	attachAutofill(startSearchInput, (v) => { navigate(v); startSearchInput.value = ""; });
	renderShortcuts();
	renderRecents();
	renderContinue();
	applyVisibility();
	renderTrust();
	if (recentsEl && window.ResizeObserver) new ResizeObserver(fitRecents).observe(recentsEl);
	if (document.fonts && document.fonts.ready) document.fonts.ready.then(fitRecents).catch(() => {});
	// Offline: one calm line, and the parts that need the connection step aside (theme.css).
	const net = () => document.documentElement.toggleAttribute("data-offline", navigator.onLine === false);
	addEventListener("online", net);
	addEventListener("offline", net);
	net();
	onOnline(() => { if (shortcutsEl) shortcutsEl.querySelectorAll(".rv-shortcut").forEach(paintHallNote); });
	history.subscribe(renderRecents);
	bookRecents.subscribe(renderContinue);
	toolRecents.subscribe(renderContinue);
	["revault:flashcards", "revault:todo", HOME_KEY].forEach((k) => storeSubscribe(k, renderContinue));
	subscribe(() => { renderShortcuts(); renderRecents(); renderContinue(); applyVisibility(); });
}
